/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import Cropper, { ReactCropperElement } from 'react-cropper';
import { CloseButton, SikdaeButton } from 'components/Button/Button';
import emptyImgIcon from 'images/storeImage/emptyImage.svg';
import deleteImgIcon from 'images/storeImage/delete_image_X_btn.svg';
import cropScissorIcon from 'images/storeImage/scissors_icon.svg';
import {
  useSaveThumbnailVerTwo,
  useSaveSignatureMenuImgVerTwo,
  useDeleteSignatureMenuImg,
  useUpdateMenuThumbnail,
  useUploadImage
} from 'hooks';
import { showUploadError } from 'apis';
import { modalMethod } from 'components';
import { getFileExtension, getImageUploadError, getUploadContentType, isUploadImageFileName, toUploadImageFileName } from 'utils';
import { ImageModal, ImageEnrollBox, CropBox, CollapseBox, RegisterImgBox } from './style';
import { buildStoreImageOrderRequest, STORE_IMAGE_UPLOAD, TStoreImageModalType } from './storeImageUpload';

interface TImageFileArr {
  idx: number;
  storeId: string;
  type: string;
  image: string;
  displayOrder: number;
  createdDate: Date;
  createdBy: string;
  lastModifiedDate?: Date;
  lastModifiedBy?: string;
}

interface TThumbnailArr {
  idx?: number | null;
  image: string;
}

type TSignatureImgType = TStoreImageModalType | 'MENU_THUMBNAIL';

interface SignatureImgProps {
  maskClosable?: boolean;
  open: boolean;
  onOk: () => void;
  onCancel: () => void;
  imgType: TSignatureImgType;
  imgFile: TImageFileArr[] | TThumbnailArr[] | null;
  sid?: string | null;
  menuId?: string | null;
}

interface TSignatureImgArrProps {
  idx?: number;
  imgInfo: string;
  name: string;
  imageUrl?: string; // 등록된 기존 이미지 URL — 신규·재크롭 이미지는 없음(업로드 대상)
}

interface TFilesArr {
  fileInfo: File | null;
}

interface TcropBlop {
  blobData?: string | Blob | null;
}

interface TImageStateProps {
  modalImgType: TSignatureImgType;
  signatureImgArr: TSignatureImgArrProps[];
  filesArr: TFilesArr[];
  blobArr: TcropBlop[];
  blobedNull: boolean;
}

interface TCropState {
  crop: boolean;
  cropImage: string | ArrayBuffer | null;
  cropBlob?: string | Blob | null;
  cropData?: {
    width: number;
    height: number;
  };
  isDeletedImage: boolean;
  clickedImgIdx?: number;
  deleteIdxArr?: number[];
  checked: boolean;
}

const { Panel } = CollapseBox;

const DEFAULTINFO = {
  THUMBNAIL: {
    title: '썸네일',
    maxImgCount: 1,
    imgWidth: 1000,
    imgHeight: 1000,
    aspectRatio: 1
  },
  SIGNATURE: {
    title: '대표',
    maxImgCount: 7,
    imgWidth: 1440,
    imgHeight: 810,
    aspectRatio: 1.777777777777777
  },
  MENU: {
    title: '메뉴판',
    maxImgCount: 7,
    imgWidth: 5000,
    imgHeight: 5000,
    aspectRatio: null
  },
  NOTICE: {
    title: '공지사항',
    maxImgCount: 1,
    imgWidth: 1440,
    imgHeight: 810,
    aspectRatio: 1.777777777777777
  },
  MEAL_PLAN: {
    title: '공지사항 팝업',
    maxImgCount: 5,
    imgWidth: 1000,
    imgHeight: 1000,
    aspectRatio: null
  },
  MENU_THUMBNAIL: {
    title: '메뉴 썸네일',
    maxImgCount: 1,
    imgWidth: 1000,
    imgHeight: 1000,
    aspectRatio: 1
  }
};

const SignatureImg = ({ maskClosable = false, open, onOk, onCancel, imgType, imgFile, sid, menuId }: SignatureImgProps) => {
  const cropperRef = useRef<ReactCropperElement>(null);
  const filePhotoRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: saveThumbnail } = useSaveThumbnailVerTwo();
  const { mutateAsync: saveSignatureMenuImg } = useSaveSignatureMenuImgVerTwo();
  const { mutateAsync: deleteSignatureMenuImg } = useDeleteSignatureMenuImg();
  const { mutateAsync: updateMenuThumbnailImg } = useUpdateMenuThumbnail();
  const { mutateAsync: uploadImage } = useUploadImage();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [cropState, setCropState] = useState<TCropState>({
    crop: false,
    cropImage: '',
    cropBlob: null,
    isDeletedImage: true,
    clickedImgIdx: 0,
    deleteIdxArr: null,
    checked: false
  });
  const [imageState, setImageState] = useState<TImageStateProps>({
    modalImgType: null,
    signatureImgArr: null,
    filesArr: null,
    blobArr: null,
    blobedNull: false
  });

  const { blobArr } = imageState;

  useEffect(() => {
    if (!imgFile || !imgFile?.length) {
      setImageState((v) => ({ ...v, modalImgType: imgType, signatureImgArr: [], blobArr: null }));
    } else if (imgFile) {
      const tempSignatureImg = [];
      const tempBlobArr = [];
      const tempFile = [];

      imgFile.forEach((el, idx) => {
        const fileName = el.image.split('/').pop();
        // 메뉴 썸네일은 S3 URL 사용 (Blob 변환 필요 없음)
        if (imgType === 'MENU_THUMBNAIL') {
          tempSignatureImg.push({ idx: el.idx ? el.idx : 0, imgInfo: el.image, name: `${imgType}_${el?.idx}` });
          tempBlobArr.push({ blobData: 'dummy' });
          tempFile.push({ fileInfo: null });
        } else {
          const metadata = { type: 'image/png' };
          const imgBlob = new Blob([el.image], metadata);

          // 재크롭 시 원본 포맷(투명 PNG 등)을 유지하도록 URL 확장자를 이름에 남긴다
          const extension = isUploadImageFileName(fileName) ? `.${getFileExtension(fileName)}` : '';
          tempSignatureImg.push({
            idx: el.idx ? el.idx : 0,
            imgInfo: el.image,
            name: `${imgType}_${el?.idx}${extension}`,
            imageUrl: el.image
          });
          tempBlobArr.push({ blobData: imgBlob });
          tempFile.push({ fileInfo: new File([tempBlobArr[idx].blobData], fileName, metadata) });
        }
      });

      setCropState((v) => ({
        ...v,
        isDeletedImage: false
      }));
      setImageState((v) => ({
        ...v,
        modalImgType: imgType,
        signatureImgArr: tempSignatureImg,
        filesArr: tempFile,
        blobArr: tempBlobArr
      }));
    }
  }, [open, imgType, imgFile]);

  useEffect(() => {
    const tempBlobedNull = blobArr?.some((el) => !el.blobData);
    setImageState((v) => ({ ...v, blobedNull: tempBlobedNull }));
  }, [blobArr]);

  useEffect(
    function stateReset() {
      if (!open) {
        setCropState((v) => ({
          ...v,
          crop: false,
          cropImage: '',
          cropBlob: null,
          isDeletedImage: true,
          checked: false,
          clickedImgIdx: 0,
          deleteIdxArr: null
        }));
        setImageState({
          modalImgType: 'THUMBNAIL',
          signatureImgArr: null,
          filesArr: null,
          blobArr: null,
          blobedNull: false
        });
      }
    },
    [open]
  );

  // 메뉴 썸네일(미라우팅 pages/Menu 전용)은 기존 v1 멀티파트 흐름 유지
  const saveMenuThumbnailImage = async () => {
    const { deleteIdxArr } = cropState;
    const { signatureImgArr } = imageState;
    if (deleteIdxArr && deleteIdxArr?.length) {
      try {
        await deleteSignatureMenuImg({ storeImageIdxList: deleteIdxArr });
      } catch (error) {
        console.log('error', error);
      }
    }

    if (signatureImgArr?.length) {
      const menuFormData = new FormData();

      const requestBlob = new Blob(
        [
          JSON.stringify({
            menuId,
            width: DEFAULTINFO[imgType].imgWidth,
            height: DEFAULTINFO[imgType].imgHeight
          })
        ],
        { type: 'application/json' }
      );
      menuFormData.append('request', requestBlob);

      const imageThumbnail = blobArr[0].blobData;
      if (imageThumbnail instanceof Blob) {
        menuFormData.append('imageThumbnail', imageThumbnail, signatureImgArr[0].name);
      }

      try {
        await updateMenuThumbnailImg({ sid, params: menuFormData });
      } catch (error) {
        console.log('error', error);
      }
    }

    modalMethod.info('이미지 등록이 완료되었습니다.');
    onOk();
  };

  const handleSaveImage = async () => {
    if (imgType === 'MENU_THUMBNAIL') {
      await saveMenuThumbnailImage();
      return;
    }

    const { deleteIdxArr } = cropState;
    const { signatureImgArr } = imageState;
    const { type, serviceName } = STORE_IMAGE_UPLOAD[imgType];
    const images = signatureImgArr ?? [];
    const uploadTargets = images.map((img, idx) =>
      img.imageUrl ? null : { file: blobArr[idx].blobData as Blob, fileName: toUploadImageFileName(img.name) }
    );

    setIsSaving(true);
    try {
      // S3 업로드를 먼저 끝낸 뒤 삭제·등록 — 업로드 실패 시 기존 이미지가 지워지지 않도록
      const imageUrls = await Promise.all(
        images.map((img, idx) =>
          uploadTargets[idx] ? uploadImage({ serviceName, storeId: sid, ...uploadTargets[idx] }) : img.imageUrl
        )
      );

      const deleteIdxList = deleteIdxArr?.filter(Boolean) ?? [];
      if (deleteIdxList.length) {
        await deleteSignatureMenuImg({ storeImageIdxList: deleteIdxList });
        // 이후 등록이 실패해 다시 저장할 때 같은 idx를 또 삭제하지 않도록
        setCropState((v) => ({ ...v, deleteIdxArr: null }));
      }

      if (imgType === 'THUMBNAIL') {
        if (uploadTargets[0]) {
          await saveThumbnail({ imageUrl: imageUrls[0] });
        }
      } else if (images.length) {
        await saveSignatureMenuImg(
          buildStoreImageOrderRequest(
            type,
            images.map((img, idx) => ({ idx: img.idx, imageUrl: imageUrls[idx] }))
          )
        );
      }
    } catch (error) {
      showUploadError(error);
      return;
    } finally {
      setIsSaving(false);
    }

    modalMethod.info('이미지 등록이 완료되었습니다.');
    onOk();
  };

  const reorderArray = <T,>(arr: T[], fromIndex: number, toIndex: number): T[] => {
    const newArr = [...arr];
    const [movedItem] = newArr.splice(fromIndex, 1);
    newArr.splice(toIndex, 0, movedItem);
    return newArr;
  };

  // 드래그 시작할 때 실행되는 핸들러
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString());
  };

  // 드래그 오버 시 기본 동작 방지
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  // 드롭 시 실행되는 핸들러
  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);

    if (dragIndex === dropIndex) return;

    setImageState((v) => ({
      ...v,
      signatureImgArr: reorderArray(v.signatureImgArr, dragIndex, dropIndex),
      filesArr: reorderArray(v.filesArr, dragIndex, dropIndex),
      blobArr: reorderArray(v.blobArr, dragIndex, dropIndex)
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { signatureImgArr, filesArr, blobArr: existingBlobArr } = imageState;
    const tempSignatureImgArr = [];
    const tempFilesArr = [];
    const tempBlobArr = [];
    const { files } = e.target;
    const maxLength = DEFAULTINFO[imgType].maxImgCount;
    const signatureImgLen = signatureImgArr ? signatureImgArr.length : 0;
    if (files && signatureImgLen + files.length > maxLength) {
      modalMethod.info(`사진은 최대 ${DEFAULTINFO[imgType].maxImgCount}개까지 등록 가능합니다.`);
      return;
    }
    const extensionError = Array.from(files ?? [])
      .map((file) => getImageUploadError(file.name))
      .find(Boolean);
    if (extensionError) {
      modalMethod.warning(extensionError);
      e.target.value = '';
      return;
    }

    if (files) {
      for (let i = 0; i < files.length; i += 1) {
        const reader = new FileReader();
        reader.readAsDataURL(files[i]);
        tempFilesArr.push({ fileInfo: files[i] });
        tempBlobArr.push({ blobData: null });
        reader.onload = (event) => {
          const img = new Image();
          if (typeof event.target.result === 'string') {
            img.src = event.target.result;
          }
          img.onload = () => {
            tempSignatureImgArr.push({ imgInfo: img.src, name: tempFilesArr[i].fileInfo.name });
            setCropState((v) => {
              return {
                ...v,
                crop: true,
                cropImage: reader.result,
                isDeletedImage: false,
                clickedImgIdx: signatureImgLen
              };
            });
            setImageState((v) => ({
              ...v,
              signatureImgArr: signatureImgArr ? [...signatureImgArr, ...tempSignatureImgArr] : tempSignatureImgArr,
              filesArr: filesArr ? [...filesArr, ...tempFilesArr] : tempFilesArr,
              blobArr: existingBlobArr ? [...existingBlobArr, ...tempBlobArr] : tempBlobArr
            }));
          };
        };
        reader.onloadend = () => {
          e.target.value = '';
        };
      }
    }
  };

  const deleteImage = (idx: number) => {
    const { isDeletedImage, deleteIdxArr } = cropState;
    const { modalImgType, filesArr, signatureImgArr } = imageState;
    const tempDeleteArr = [];
    if (modalImgType === 'THUMBNAIL') {
      signatureImgArr.pop();
      filesArr.pop();
      blobArr.pop();
    } else {
      tempDeleteArr.push(signatureImgArr[idx].idx);
      signatureImgArr.splice(idx, 1);
      filesArr.splice(idx, 1);
      blobArr.splice(idx, 1);
    }

    if (!signatureImgArr) {
      setCropState((v) => ({ ...v, crop: false, isDeletedImage: true }));
    }

    if (!isDeletedImage) {
      setCropState((v) => ({
        ...v,
        cropImage: '',
        cropBlob: null,
        cropData: null,
        deleteIdxArr: deleteIdxArr ? [...deleteIdxArr, ...tempDeleteArr] : tempDeleteArr
      }));
    }
    setImageState((v) => ({
      ...v,
      signatureImgArr,
      filesArr
    }));
  };

  const onCropData = () => {
    const { clickedImgIdx } = cropState;
    const { signatureImgArr } = imageState;
    if (cropperRef) {
      const imageElement = cropperRef?.current;
      const { cropper } = imageElement;
      const { imgWidth, imgHeight, aspectRatio } = DEFAULTINFO[imgType];
      // 자유 비율(aspectRatio null)은 규격을 최대 크기로만 적용 — 고정 크기로 그리면 크롭 비율과 달라 이미지가 늘어남
      const canvas = cropper.getCroppedCanvas(
        aspectRatio ? { width: imgWidth, height: imgHeight } : { maxWidth: imgWidth, maxHeight: imgHeight }
      );

      // 업로드 파일명 확장자와 같은 포맷으로 인코딩해야 presigned Content-Type과 실제 바이트가 일치한다
      const encodeType = getUploadContentType(toUploadImageFileName(signatureImgArr[clickedImgIdx].name));

      canvas.toBlob(
        (blob) => {
          const dupSignatureImgArr = Array.from(signatureImgArr);
          const dupBlobArr = Array.from(blobArr);

          dupSignatureImgArr[clickedImgIdx] = { imgInfo: canvas.toDataURL(), name: dupSignatureImgArr[clickedImgIdx].name };
          dupBlobArr[clickedImgIdx] = { blobData: blob };

          setCropState((v) => ({ ...v, crop: false, isDeletedImage: false }));
          setImageState((v) => ({ ...v, signatureImgArr: dupSignatureImgArr, blobArr: dupBlobArr }));
        },
        encodeType,
        0.92
      );
    }
  };

  const onCrop = (e) => {
    const { detail } = e;
    setCropState((v) => ({
      ...v,
      cropData: {
        width: parseInt(detail.width, 10),
        height: parseInt(detail.height, 10)
      }
    }));
  };

  const onCheckboxChange = (e: CheckboxChangeEvent) => {
    setCropState((v) => ({ ...v, checked: e.target.checked }));
  };

  const saveBtnDisabled = () => {
    const { checked } = cropState;
    const { signatureImgArr, blobedNull } = imageState;
    const isImgNullWhenThumbnail = imgType === 'THUMBNAIL' && !signatureImgArr?.length;
    const isImgNullWhenSignatureMenu = (imgType === 'SIGNATURE' || imgType === 'MENU') && !signatureImgArr?.length;

    if (isImgNullWhenThumbnail) {
      return true;
    }
    if (isImgNullWhenSignatureMenu && checked) {
      return false;
    }
    return !!(!checked || blobedNull);
  };

  const { isDeletedImage, clickedImgIdx, crop, checked } = cropState;
  const { signatureImgArr } = imageState;

  return (
    <ImageModal
      title={<div className="header">이미지 등록</div>}
      maskClosable={maskClosable}
      open={open}
      onOk={onOk}
      onCancel={onCancel}
      footer={[
        <div className="footer" key="footer">
          <CloseButton className="close-btn" onClick={onCancel}>
            닫기
          </CloseButton>
          <SikdaeButton onClick={handleSaveImage} disabled={saveBtnDisabled() || isSaving}>
            저장
          </SikdaeButton>
        </div>
      ]}
    >
      <div className="modal-box">
        <div className="title">{DEFAULTINFO[imgType].title} 이미지 등록</div>
        <div className="title-description">
          {imgType === 'MENU_THUMBNAIL'
            ? '메뉴 썸네일 이미지를 등록해 주세요!'
            : `우리가게 ${DEFAULTINFO[imgType].title} 이미지를 등록해 주세요!`}
        </div>
      </div>
      <div className="modal-box">
        <div className="modal-inner-box">
          <span className="guide-description">사진을 등록해주세요</span>
          <span className="guide-mandatory">&#42;</span>
        </div>
        <ImageEnrollBox isImage={!signatureImgArr || signatureImgArr.length === 0}>
          <div
            style={signatureImgArr?.length > 0 ? { marginBottom: '20px' } : null}
            className="empty-image-container"
            role="presentation"
            onClick={() => handleFileChange}
          >
            <label htmlFor="img-files">
              <div className="container-center">
                <img src={emptyImgIcon} alt="img-preview" />
                <div className="image-count">
                  <span>{signatureImgArr ? signatureImgArr.length : 0}</span>
                  <span>/{DEFAULTINFO[imgType].maxImgCount}</span>
                </div>
              </div>
              <input
                id="img-files"
                className="thumbnail-file"
                ref={filePhotoRef}
                type="file"
                accept=".jpg, .jpeg, .png"
                onChange={handleFileChange}
                multiple
              />
            </label>
          </div>
          {signatureImgArr
            ? signatureImgArr.map((el, idx) => {
                return (
                  <RegisterImgBox key={el.name} imgIdx={idx} blobData={blobArr ? blobArr[idx]?.blobData : null}>
                    <div
                      className="enrolled-image-container"
                      role="presentation"
                      onClick={() => setCropState((v) => ({ ...v, crop: true, clickedImgIdx: idx }))}
                      draggable={imgType !== 'THUMBNAIL'}
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, idx)}
                    >
                      {!isDeletedImage ? (
                        <img
                          className="enrolled-image-delete-btn"
                          alt="btn delete image"
                          aria-hidden="true"
                          src={deleteImgIcon}
                          onClick={() => deleteImage(idx)}
                        />
                      ) : null}
                      <div className="enrolled-image-numbering">{idx + 1}</div>
                      <div className="enrolled-image-dimmed" />
                      <div className="enrolled-image">
                        <img
                          id="img-preview"
                          src={el.imgInfo}
                          alt={el.name}
                          crossOrigin={imgType === 'MENU_THUMBNAIL' ? undefined : 'anonymous'}
                        />
                      </div>
                      <img src={cropScissorIcon} alt="crop-icon" className="enrolled-image-crop" />
                    </div>
                  </RegisterImgBox>
                );
              })
            : null}
        </ImageEnrollBox>
        {crop
          ? signatureImgArr
              .filter((el, idx) => idx === clickedImgIdx)
              .map((el, idx) => {
                return (
                  <CropBox key={el.name} imgType={imgType} imgCount={signatureImgArr?.length}>
                    <div className="crop-container">
                      <Cropper
                        className="cropper"
                        key={el.name}
                        crop={onCrop}
                        src={el.imgInfo as string}
                        guides={false}
                        ref={cropperRef}
                        viewMode={1}
                        zoomOnWheel
                        aspectRatio={DEFAULTINFO[imgType].aspectRatio}
                        autoCropArea={0.99}
                      />
                    </div>
                    <div className="crop-container" key="img-control">
                      <CloseButton className="close-btn" width={325} onClick={() => deleteImage(idx)}>
                        사진 삭제
                      </CloseButton>
                      <SikdaeButton width={325} onClick={onCropData}>
                        사진 자르기
                      </SikdaeButton>
                    </div>
                  </CropBox>
                );
              })
          : null}
      </div>
      <div className="modal-box">
        <div className="modal-inner-box">
          <span className="guide-description">안내사항 확인</span>
          <span className="guide-mandatory">&#42;</span>
        </div>
        <CollapseBox
          defaultActiveKey={['1']}
          expandIconPosition="end"
          collapsible="icon"
          expandIcon={({ isActive }) => <div className="expand-icon">{isActive ? '닫기' : '내용확인'}</div>}
          checkbox={checked}
        >
          <Panel
            header={
              <Checkbox className="checkbox-round" name="guide-checked" onChange={onCheckboxChange} checked={checked}>
                안내사항을 확인하였습니다.
              </Checkbox>
            }
            key="1"
          >
            <div className="checkbox-inner-content">
              <div>
                &#8226; 관련 사진만 등록해 주세요. 부적절하거나 제휴점과 관련이 없는 사진은 내부 운영 원칙에 따라 삭제될 수
                있습니다.
              </div>
              <div>
                &#8226; 직접 업로드 이미지는 {DEFAULTINFO[imgType].imgWidth} * {DEFAULTINFO[imgType].imgHeight} 크기 이하의
                jpg/png 형식의 파일을 등록하실 수 있습니다.
              </div>
              <div>
                &#8226; {DEFAULTINFO[imgType].title} 이미지는 최대 {DEFAULTINFO[imgType].maxImgCount}개 등록 가능합니다.
              </div>
              {imgType !== 'THUMBNAIL' && imgType !== 'NOTICE' && imgType !== 'MENU_THUMBNAIL' ? (
                <div>&#8226; 이미지 순서 조정이 필요한 경우 파일을 1개씩 순서대로 업로드 해야 합니다.</div>
              ) : null}
              {imgType === 'NOTICE' ? <div>&#8226; 공지 이미지의 삭제는 등록된 이미지를 삭제 후 저장해야 가능합니다.</div> : null}
            </div>
          </Panel>
        </CollapseBox>
      </div>
    </ImageModal>
  );
};

export default SignatureImg;
